import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RequireMenuAccess from '../common/RequireMenuAccess';
import RTButton from '../common/RTButton';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError, toastSuccess, ToastConfirm } from '../common/toast';
import '../common/rtCommon.css';
import './RoleSettings.css';
import RTPageLayout from '../common/RTPageLayout';

const RoleSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [menus, setMenus] = useState([]);
  const [roleMeta, setRoleMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expanded, setExpanded] = useState({});

  const loadRoles = async () => {
    const res = await apiGet('/roles');
    const list = res.data?.roles || [];
    setRoles(list);
    if (!selectedRoleId && list.length) {
      setSelectedRoleId(list[0].id);
    }
  };

  const loadRoleDetail = async (roleId) => {
    if (!roleId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/roles/${roleId}/permissions`);
      setRoleMeta(res.data?.role || null);
      setMenus(res.data?.menus || []);
      const exp = {};
      (res.data?.menus || []).forEach((m) => {
        exp[m.key] = Boolean(m.enabled);
      });
      setExpanded(exp);
    } catch (err) {
      toastError(
        err?.response?.data?.detail || 'Failed to load role permissions',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles().catch((err) =>
      toastError(err?.response?.data?.detail || 'Failed to load roles'),
    );
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
      loadRoleDetail(selectedRoleId);
    }
  }, [selectedRoleId]);

  const toggleMenu = (menuKey, enabled) => {
    setMenus((prev) =>
      prev.map((m) => (m.key === menuKey ? { ...m, enabled } : m)),
    );
    if (enabled) {
      setExpanded((prev) => ({ ...prev, [menuKey]: true }));
    }
  };

  const togglePermission = (menuKey, permKey, enabled) => {
    setMenus((prev) =>
      prev.map((m) => {
        if (m.key !== menuKey) return m;
        return {
          ...m,
          permissions: (m.permissions || []).map((p) =>
            p.key === permKey ? { ...p, enabled } : p,
          ),
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!selectedRoleId || roleMeta?.is_locked) return;
    setSaving(true);
    try {
      await apiPost(`/roles/${selectedRoleId}/permissions/update`, {
        menus: menus.map((m) => ({
          key: m.key,
          enabled: Boolean(m.enabled),
          permissions: (m.permissions || []).map((p) => ({
            key: p.key,
            enabled: Boolean(p.enabled),
          })),
        })),
      });
      toastSuccess('Permissions saved');
      await loadRoleDetail(selectedRoleId);
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await apiPost('/roles', { name: newName.trim() });
      toastSuccess('Role created with staff permissions enabled (PG portal off)');
      setNewName('');
      setShowCreate(false);
      await loadRoles();
      if (res.data?.role?.id) {
        setSelectedRoleId(res.data.role.id);
      }
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to create role');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    setShowDeleteConfirm(false);
    if (!selectedRoleId || roleMeta?.is_locked) return;
    const deletedId = selectedRoleId;
    try {
      const res = await apiPost(`/roles/${deletedId}/delete`);
      toastSuccess(res.data?.message || 'Role deleted');
      const rolesRes = await apiGet('/roles');
      const list = rolesRes.data?.roles || [];
      setRoles(list);
      const next = list[0] || null;
      setSelectedRoleId(next ? next.id : null);
      if (!next) {
        setMenus([]);
        setRoleMeta(null);
      }
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to delete role');
    }
  };

  const locked = Boolean(roleMeta?.is_locked);

  return (
    <RequireMenuAccess>
      <RTPageLayout
        activePath={location.pathname}
        title="Role Settings"
        actions={
          <>
            <RTButton
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/access-control')}
            >
              ← Back
            </RTButton>
            <RTButton
              permissionKey="access-control.role_settings"
              onClick={() => setShowCreate(true)}
            >
              Create Role
            </RTButton>
          </>
        }
        mainSection={true}
      >
        <div className="role-settings-sidebar">
          <h3>Roles</h3>
          <ul className="role-list">
            {roles.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={
                    selectedRoleId === r.id ? 'role-item active' : 'role-item'
                  }
                  onClick={() => setSelectedRoleId(r.id)}
                >
                  {r.name}
                  {r.is_locked ? (
                    <span className="locked-badge">Locked</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="role-settings-matrix">
          {loading ? (
            <p>Loading…</p>
          ) : (
            <>
              <div className="role-matrix-header">
                <h3>
                  {roleMeta?.name || 'Select a role'}
                  {locked ? <span className="locked-badge">Locked</span> : null}
                </h3>
                <div className="role-matrix-actions">
                  {!locked ? (
                    <>
                      <RTButton
                        permissionKey="access-control.role_settings"
                        onClick={handleSave}
                        disabled={saving || !selectedRoleId}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </RTButton>
                      <RTButton
                        permissionKey="access-control.role_settings"
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={!selectedRoleId}
                      >
                        Delete Role
                      </RTButton>
                    </>
                  ) : null}
                </div>
              </div>
              {locked ? (
                <p className="role-locked-note">
                  This role is locked and cannot be edited.
                </p>
              ) : null}
              <div className="role-menu-accordion">
                {menus.map((menu) => (
                  <div key={menu.key} className="role-menu-block">
                    <div className="role-menu-header">
                      <label className="role-menu-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(menu.enabled)}
                          disabled={locked}
                          onChange={(e) =>
                            toggleMenu(menu.key, e.target.checked)
                          }
                        />
                        <strong>{menu.label}</strong>
                      </label>
                      {menu.enabled && (menu.permissions || []).length > 0 ? (
                        <button
                          type="button"
                          className="role-menu-expand"
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [menu.key]: !prev[menu.key],
                            }))
                          }
                        >
                          {expanded[menu.key] ? 'Hide' : 'Show'} permissions
                        </button>
                      ) : null}
                    </div>
                    {menu.enabled && expanded[menu.key] ? (
                      <ul className="role-perm-list">
                        {(menu.permissions || []).map((p) => (
                          <li key={p.key}>
                            <label>
                              <input
                                type="checkbox"
                                checked={Boolean(p.enabled)}
                                disabled={locked}
                                onChange={(e) =>
                                  togglePermission(
                                    menu.key,
                                    p.key,
                                    e.target.checked,
                                  )
                                }
                              />
                              {p.label}
                            </label>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </RTPageLayout>
      {showCreate ? (
        <div
          className="role-create-overlay"
          onClick={() => !creating && setShowCreate(false)}
        >
          <div
            className="role-create-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Create Role</h3>
            <label>
              Display name
              <input
                placeholder="e.g. Manager or Night Warden"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
            </label>
            <div className="role-create-actions">
              <RTButton
                variant="secondary"
                onClick={() => setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </RTButton>
              <RTButton
                permissionKey="access-control.role_settings"
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
              >
                {creating ? 'Creating…' : 'Create'}
              </RTButton>
            </div>
          </div>
        </div>
      ) : null}
      <ToastConfirm
        show={showDeleteConfirm}
        message={
          roleMeta
            ? `Delete role "${roleMeta.name}" (${roleMeta.code})? This cannot be undone.`
            : 'Delete this role?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="#b91c1c"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </RequireMenuAccess>
  );
};

export default RoleSettings;
